import { useCallback, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';

export function useStatusLabel() {
  const { t } = useTranslate();

  const statusLabel = useMemo<Record<Todo.Status, string>>(() => {
    return {
      1: t('domain.task.status.unclaimed'),
      2: t('domain.review.status.inProgress'),
      3: t('domain.task.status.completed'),
      4: t('domain.review.status.pending'),
    };
  }, [t]);

  const getStatusLabel = useCallback(
    (status: Todo.Status) => statusLabel[status] || t('domain.task.status.unknown'),
    [statusLabel, t],
  );

  return {
    statusLabel,
    getStatusLabel,
    t,
  };
}

export const officialTypeId = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
export const isOfficialType = (type: number): type is Todo.OfficialType =>
  officialTypeId.includes(type);

export function useTypeLabel() {
  const { t } = useTranslate();

  const typeLabel = useMemo<Record<Todo.OfficialType, string>>(() => {
    return {
      0: t('todo.买家索要收据或发票'),
      1: t('product_cannot_be_used'),
      2: t('todo.商品少发错发'),
      3: t('todo.买家希望换货'),
      4: t('todo.买家希望退款'),
      5: t('todo.买家希望退货'),
      6: t('todo_type_6'),
      7: t('弃单可发起争议'),
      8: t('额外收取运费'),
      9: t('订单超时未发货'),
      10: t('订单超时未揽收'),
      11: t('运输过久'),
      12: t('达人建联'),
      13: t('aide.取消订单'),
      14: t('aide.派送失败'),
    };
  }, [t]);

  const getTypeLabel = useCallback(
    (status: Todo.Type) => {
      if (isOfficialType(status)) {
        return typeLabel[status] || t('domain.task.status.unknown');
      }

      return status;
    },
    [typeLabel, t],
  );

  return {
    typeLabel,
    getTypeLabel,
    t,
  };
}

export function useGetTodoTypeLabel() {
  const { t, typeLabel } = useTypeLabel();

  const getTodoTypeLabel = useCallback(
    (type: Todo.Type, typeName?: string) => {
      if (type === -998) {
        return t('custom');
      }
      if (type === -999) {
        return t('marketing.已删除');
      }
      return typeLabel[type as keyof typeof typeLabel] || typeName;
    },
    [t, typeLabel],
  );

  return {
    t,
    typeLabel,
    getTodoTypeLabel,
  };
}

export function useResultTypeLabel() {
  const { t } = useTranslate();

  const resultTypeLabel = useMemo(() => {
    return {
      RECEIPT: t('type0Result.RECEIPT'),
      RESEND_GOODS: t('type1Result.RESEND_GOODS'),
      OFFER_COMPENSATION: t('type1Result.OFFER_COMPENSATION'),
      REFUND: t('type1Result.REFUND'),
      REISSUE_GOODS: t('type2Result.REISSUE_GOODS'),
      PARTIAL_REFUND: t('type2Result.PARTIAL_REFUND'),
      NON_PROBLEM: t('resultFallback.NON_PROBLEM'),
      OTHER: t('resultFallback.OTHER'),
      INVOICE: t('issue_a_receipt_or_invoice'),
      GOODS_NORMAL: t('未少发错发'),
      RETURN_GOODS_REFUND: t('退货退款'),
      USE_METHOD_ERROR: t('使用方法错误'),
      SUBMITTED_DISPUTE: t('已提交争议，平台处理中'),
      NO_DISPUTE: t('此退单无需争议'),
      NO_DISPUTE_TRADE_CLOSED: t('退单已关闭，未提交争议'),
      DELIVERY_FAILED_RESEND: t('type2Result.REISSUE_GOODS'),
      REACHED: t('已送达'),
      COLLECTED: t('已揽收'),
      CANCEL_OUT_STOCK: t('商品缺货，引导买家取消订单'),
      DELIVERED: t('aide.已发货'),
      APPEAL_SHOPEE: t('已向Shopee申诉'),
      DISPUTE_SUCCESS: t('争议成功'),
      DISPUTE_FAIL: t('争议失败'),
      REISSUE_GOODS_ASK_BUYER: t('询问是否接受补发'),
      REISSUE_GOOD_ASK_BUYER_ADDRESS: t('买家同意补发，尚未提供补发地址信息'),
      REISSUE_GOOD_GET_BUYER_ADDRESS: t('买家同意补发，已提供补发地址信息'),
      REISSUE_GOOD_SENDED: t('已补发商品'), // 支持参数
      PARTIAL_REFUND_ASK_BUYER: t('询问是否接受部分退款'),
      PARTIAL_REFUND_FINISH: t('已完成退款'),
      REFUND_ASK_BUYER: t('询问是否接受仅退款'),
      REFUND_BUYER_REQ: t('买家已发起退款申请'),
      REFUND_FINISH: t('买家退款申请已通过'),
      ONLY_REFUND: t('aide.仅退款'),
      OFFER_COUPONS: t('提供优惠券'),
      CONTACT_LOGISTICS: t('联系物流'),
      REFUND_SUGGEST: t('建议退款'),
      EXPEDITED_DELIVERY: t('加急发货'),
      OUT_STOCK: t('商品缺货'),
      RUSH_LOGISTICS: t('催促物流'),
      EXPEDITED_DELIVERY_TELL_BUYER: t('告知买家已经加急发货'),
      OUT_STOCK_TELL_BUYER_CANCEL: t('引导买家取消订单'),
      OUT_STOCK_ASK_BUYER_WAIT: t('询问买家是否愿意等待'),
      REACHED_LOGISTICS: t('说明物流已经送达'),
      COLLECTED_TELL_RESULT: t('告知订单已揽收'),
      CONTACT_LOGISTICS_SUGGEST: t('建议联系物流'),
      REFUND_SUGGEST_BUYER: t('建议买家退款'),
      RESEND_GOODS_ASK_BUYER: t('询问是否接受重发'),
      RESEND_GOOD_ASK_BUYER_ADDRESS: t('买家同意重发，尚未提供地址信息'),
      RESEND_GOOD_GET_BUYER_ADDRESS: t('买家同意重发，已提供地址信息'),
      RESEND_GOOD_SENDED: t('商品已重新发货'),
      OFFER_COMPENSATION_ASK_BUYER: t('询问是否接受补偿金'),
      OFFER_COMPENSATION_TELL_PROCESS: t('告知补偿流程'),
      REFUSE_RETURN_GOODS_ASK_BUYER: t('询问是否接受退货退款'),
      REFUSE_RETURN_GOODS_TELL_PROCESS: t('买家同意退货退款，说明流程'),
      REFUSE_RETURN_GOODS_BUYER_REQ: t('买家已申请退货退款'),
      REFUSE_RETURN_GOODS_FINISH: t('买家完成退货'),
      USE_METHOD_ERROR_TELL_METHOD: t('说明正确使用方法'),
      OFFER_COUPONS_ASK_BUYER: t('询问是否接受优惠券'),
      OFFER_COUPONS_SENT: t('已发送优惠券'),
      PARTIAL_REFUND_TELL_PROCESS: t('买家同意部分退款，说明流程'),
      REFUND_WAIT_REQ: t('买家同意仅退款，尚未申请'),
      DELIVERED_ORDER: t('订单已经发货'),
      RUSH_LOGISTICS_TELL_RESULT: t('告知已催促物流公司揽收'),
      // type: 13 start
      ASK_REASON: t('询问原因'),
      REJECT_CANCEL: t('拒绝取消'),
      AGREE_CANCEL: t('同意取消'),
      NON_BUYER_CANCEL: t('非买家主动取消'),
      ORDER_CANCEL_ASK_BUYER: t('询问取消原因'),
      ORDER_CANCEL_REJECT_DELIVERED: t('已发货无法取消'),
      ORDER_CANCEL_REJECT_REFUND: t('建议拒收/退货'),
      ORDER_CANCEL_REJECT_OTHER: t('trade_return_reason_OTHER'),
      ORDER_CANCEL_AGREE_SEND_APPLY: t('让买家自行申请'),
      ORDER_CANCEL_AGREE_APPLY_PROCESS: t('申请正在处理中'),
      ORDER_CANCEL_AGREE: t('已同意取消申请'),
      ORDER_CANCEL_TELL_REASON: t('告知取消原因'),
      // type: 13 end
    } as Record<Todo.ResultType, string>;
  }, [t]);

  const getResultTypeLabel = useCallback(
    (resultType: Todo.ResultType) => resultTypeLabel[resultType] || resultType,
    [resultTypeLabel],
  );

  return {
    resultTypeLabel,
    getResultTypeLabel,
    t,
  };
}

export function useResultTypeTipsLabel() {
  const { t } = useTranslate();

  const resultTypeTipsLabel = useMemo<Partial<Record<Todo.ResultType, string>>>(() => {
    return {
      INVOICE: t('可记录买家的开票需求'),
      REISSUE_GOODS: t('请记录买家遇到的问题与补发信息'),
      PARTIAL_REFUND: t('请记录买家遇到的问题与退款金额'),
      RESEND_GOODS: t('请记录买家遇到的问题与重发信息'),
      OFFER_COMPENSATION: t('请记录买家遇到的问题与补偿金额'),
      RETURN_GOODS_REFUND: t('请记录买家遇到的问题'),
    };
  }, [t]);

  const getResultTypeTipsLabel = useCallback(
    (resultType: Todo.ResultType) => resultTypeTipsLabel[resultType] || t('trade.createComment'),
    [resultTypeTipsLabel, t],
  );

  return {
    resultTypeTipsLabel,
    getResultTypeTipsLabel,
    t,
  };
}
